/**
 * The BluePenguin Payments Terms of Service body, moved verbatim from the old BankAgreement.tsx.
 *
 * This is a separate legal instrument from the MSA — a different counterparty (BluePenguin, and
 * Pathward as its partner bank) with its own signature record, version and PDF. It is presented
 * alongside the MSA on one step, but it is never merged into it.
 *
 * Content only; the disclosure accordion, its own checkbox and the submit live in Agreements.tsx.
 * The text is kept verbatim, including its headings, to stay in step with the PDF rendered
 * server-side (rawBankAgreementPdfHtml in tach-architecture).
 */

/**
 * 1.1 corrects "Bank's decision" to "BluePenguin and/or Bank's decision", matching Blue Penguin's
 * specimen. The words changed, so the version must change with them — the ~119 signatures already
 * recorded as 1.0 carry the earlier wording, and a version that no longer identifies a specific
 * text cannot evidence what any given customer accepted.
 *
 * Bump this on any change to the text below, and mirror it in tach-mobile.
 */
export const BANK_AGREEMENT_VERSION = '1.1';

function BankAgreementText() {
  return (
    <div className="space-y-6 text-sm text-gray-800">
      <div className="text-center">
        <h3 className="text-lg font-bold">
          BLUEPENGUIN PAYMENTS TERMS OF SERVICE
        </h3>
      </div>

      <div>
        <h4 className="mb-2 font-bold">
          BluePenguin Payments Terms of Service
        </h4>

        <p className="mb-4">
          Trucking Automated Clearing House, LLC ("TACH") has contracted with
          BluePenguin Payments, Inc., a Florida corporation ("BluePenguin") to
          offer certain business payment processing services ("BluePenguin
          Services") to TACH's customers, including but not necessarily limited
          to virtual and physical payment card creation and processing and
          Automated Clearing House (ACH) processing services. In connection with
          your company's use of TACH's services, you can opt to use the payment
          services provided by BluePenguin to your business.
        </p>

        <p className="mb-4">
          Your customer relationship with TACH is not a guarantee of access to
          the BluePenguin Services. To utilize the BluePenguin Services, you
          must furnish any and all required information for BluePenguin and its
          partner bank Pathward, N.A. ("Bank") to perform initial and ongoing
          Know-Your-Customer processing in order to determine your qualification
          to use (or continue using) the BluePenguin Services. BluePenguin
          and/or Bank's decision to provide the Services to you shall be final.
        </p>

        <p className="mb-4">
          Your access to, and ongoing usage of, the BluePenguin Services shall
          at all times be governed by the BluePenguin terms of service
          ("BluePenguin Terms"), located at{' '}
          <a
            href="https://www.bluepenguin.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            https://www.bluepenguin.com/terms
          </a>
          ; and by the Bank Payment Service Terms ("Bank Terms"), located at{' '}
          <a
            href="https://www.bluepenguin.com/pathward-terms-reseller"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            https://www.bluepenguin.com/pathward-terms-reseller
          </a>{' '}
          as either might change from time to time, both incorporated here in
          full by reference. For the avoidance of doubt, the term "Service
          Provider" in the Bank Terms refers to TACH. If there are any conflicts
          between the terms of your agreement with TACH and the BluePenguin
          Terms and/or Bank Terms, the BluePenguin Terms and/or Bank Terms shall
          control to the extent the conflict relates to the BluePenguin
          Services. If you cannot or don't wish to comply with the BluePenguin
          Terms in their entirety, including the applicable Bank Terms, your
          sole recourse is not to use the BluePenguin Services. Your initials or
          signature below indicate that you understand and are authorized by
          your company to agree and bind your company to the BluePenguin Terms
          and applicable Bank Terms.
        </p>
      </div>
    </div>
  );
}

export default BankAgreementText;
